// src/components/layout/MainContainer.jsx
import React, { useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useConversation } from '@/managers/ConvoManager';
import { authManager } from '@/managers/AuthManager';
import { botManager } from '@/managers/BotManager';
import { usePanelManager, PanelTypes, RightSidebarViews } from '@/managers/PanelManager';
import { useChatFeed } from '@/managers/ChatFeedManager/ChatFeedManager';
import Header from '@/components/layout/Header';
import ChatColumn from '@/components/layout/ChatColumn/ChatColumn';
import InputArea from '@/components/layout/InputArea';
import Sidebar from '@/components/layout/sidebar/SidebarContainer';
import EmptyState from '@/components/layout/EmptyState/EmptyState';
import ShelbulaWorkspaces from '@/components/layout/RightPanel/shelbulaworkspaces/ShelbulaWorkspaces';
import SnippetsDrawer from '@/components/tools/snippets/SnippetsDrawer';
import { canAccessWorkspaces } from '@/utils/workspaceAccess';
import { isPrivateWorkspaceContextId } from '@/utils/workspaceContext';
import ToolsGuidePanel from '@/components/tools/ToolsGuidePanel';
import { MessageCircleQuestion } from 'lucide-react';
import SupportModal from '@/components/layout/SupportModal';
import { useTheme } from '@/managers/ThemeProvider';
import { useProjectCompletionNotification } from '@/components/layout/RightPanel/useProjectCompletionNotification';
import { motion, AnimatePresence } from 'framer-motion';
import { keyStateManager } from '@/managers/zKeysManager';


/* ========================
 * useKeys - Custom hook to track real user-provided API keys
 * ======================== */
const useKeys = () => {
    const [keyState, setKeyState] = useState(() => keyStateManager.getState());
    useEffect(() => {
        const unsub = keyStateManager.subscribe(setKeyState);
        return unsub;
    }, []);
    return keyState;
};


/* ========================
 * useBot - Custom hook to derive bot-related state
 * Leverages BotManager as the SINGLE SOURCE OF TRUTH for bot/key state.
 * ======================== */
const useBot = () => {
    const [botData, setBotData] = useState(() => botManager.getState());

    useEffect(() => {
        const unsubBot = botManager.subscribe(setBotData);
        return () => {
            unsubBot();
        };
    }, []);

    const {
        allBots,
        allPlatformModels,
        currentBotId,
        currentModelId,
        activeBotConfig,
        isLoading,
        isInitialized,
        hasPlatformKeys,
    } = botData;

    const currentBot = useMemo(() => allBots?.find(b => b.id === currentBotId) || null, [allBots, currentBotId]);
    const currentModel = useMemo(() => allPlatformModels?.find(m => m.id === currentModelId) || null, [allPlatformModels, currentModelId]);

    const availablePlatforms = useMemo(() => {
        if (!allPlatformModels) return {};
        return allPlatformModels.reduce((acc, model) => {
            const platformKey = model.platform;
            if (!acc[platformKey]) acc[platformKey] = [];
            acc[platformKey].push(model);
            return acc;
        }, {});
    }, [allPlatformModels]);

    return {
        // Core data
        currentBot,
        currentModel,
        availablePlatforms,
        allBots: allBots || [],
        allPlatformModels: allPlatformModels || [],
        currentBotId,
        currentModelId,
        activeBotConfig,
        // Status fields
        hasKeys: hasPlatformKeys,
        isLoading,
        isInitialized,
    };
};

/* ========================
 * useAuth - Custom hook to access auth state and settings from AuthManager
 * Provides a reactive interface to settings and auth status stored in AuthManager
 * ======================== */
const useAuth = () => {
    const [authState, setAuthState] = useState(() => authManager.getState());

    useEffect(() => {
        const unsubscribe = authManager.subscribe(setAuthState);
        return unsubscribe;
    }, []);

    const updateSettings = useCallback((updates) => {
        return authManager.updateSettings(updates);
    }, []);

    return {
        ...authState,
        settings: authState.settings,
        updateSettings,
        chatFontSize: authState.settings.zPreferences?.chatFontSize || 'md',
        codeFontSize: authState.settings.zPreferences?.codeFontSize || 'md',
        setChatFontSize: (size) => updateSettings({ zPreferences: { chatFontSize: size } }),
        setCodeFontSize: (size) => updateSettings({ zPreferences: { codeFontSize: size } })
    };
};


const MOBILE_BREAKPOINT = 768;

const SHARED_RIGHT_SIDEBAR_CONFIG = {
    defaultWidth: 65,
    minWidth: 40,
    maxWidth: 90,
    minWidthPx: 600,
};

const PANEL_CONFIGS = {
    [PanelTypes.PORTAL]: SHARED_RIGHT_SIDEBAR_CONFIG,
    [PanelTypes.NOTES]: {
        defaultWidth: 30,
        minWidth: 28,
        maxWidth: 60,
        minWidthPx: 300
    },
    [PanelTypes.SNIPPETS]: {
        defaultWidth: 30,
        minWidth: 20,
        maxWidth: 70,
        minWidthPx: 280
    },
    [PanelTypes.REACT_MOCKUP]: {
        defaultWidth: 50,
        minWidth: 20,
        maxWidth: 100,
        minWidthPx: 400
    },
    [PanelTypes.CODER]: SHARED_RIGHT_SIDEBAR_CONFIG
};

const getSharedPanelSizingKey = (panelType) => {
    if (panelType === PanelTypes.PORTAL || panelType === PanelTypes.CODER) {
        return PanelTypes.CODER;
    }

    return panelType;
};

/* ========================
 * MainContainer - Core layout component for the chat interface
 * Coordinates various managers and UI elements
 * ======================== */
const MainContainer = () => {
    const navigate = useNavigate();

    // =============================================
    // Hook Initializations - Get full reactive state from managers
    // =============================================
    const authState = useAuth();
    const botState = useBot();
    const keyState = useKeys();
    const { createConversation, updateConversation, currentConversationId, currentConversation, conversations, apiKeys } = useConversation();
    const { isPanelOpen, togglePanelWithMemory, panelWidths, setPanelWidth, rightSidebarView } = usePanelManager();
    const { toggleTheme } = useTheme();
    const { sendMessage } = useChatFeed();
    const { hasNewCompletion, resetNotification } = useProjectCompletionNotification();

    // =============================================
    // Centralized State & Destructuring
    // =============================================
    const { tier, chatFontSize, codeFontSize } = authState;
    const { currentBot, currentModel } = botState;

    const platformForCurrentConvo = currentConversation?.botPlatform;
    const keyIsMissingForConvo = platformForCurrentConvo && apiKeys
        ? !apiKeys[platformForCurrentConvo.toUpperCase()]
        : false;

    // Gate on real user-stored keys (keyStateManager), not hasPlatformKeys.
    const showKeysView = (keyState.isKeysReady && !keyState.hasKeys) || keyIsMissingForConvo;
    const platformNeeded = keyIsMissingForConvo ? platformForCurrentConvo : null;


    // =============================================
    // Refs
    // =============================================
    const memorySliderRef = useRef(null);
    const portalRef = useRef(null);
    const toolsGuideRef = useRef(null);
    const resizeRef = useRef(null);

    // =============================================
    // UI State - Only what MainContainer actually needs
    // =============================================
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [isMemorySliderOpen, setIsMemorySliderOpen] = useState(false);
    const [isResizing, setIsResizing] = useState(false);
    const [resizeStartX, setResizeStartX] = useState(0);
    const [resizeStartWidth, setResizeStartWidth] = useState(0);
    const [resizingPanel, setResizingPanel] = useState(null);
    const [isToolsGuideOpen, setIsToolsGuideOpen] = useState(false);
    const [isSupportOpen, setIsSupportOpen] = useState(false);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < MOBILE_BREAKPOINT);

    // =============================================
    // Enhanced Mobile Detection & Strategy
    // =============================================
    const [mobileStrategy, setMobileStrategy] = useState('auto');

    const effectiveMobileView = useMemo(() => {
        if (mobileStrategy === 'force-mobile') return true;
        if (mobileStrategy === 'force-desktop') return false;
        return isMobileView;
    }, [mobileStrategy, isMobileView]);

    const shouldUseMobileContentStrategy = effectiveMobileView;

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth < MOBILE_BREAKPOINT);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // =============================================
    // Active Panel Detection
    // =============================================
    const showPersistentWorkspaceStage = useMemo(() => {
        return !shouldUseMobileContentStrategy && canAccessWorkspaces(authState.user);
    }, [shouldUseMobileContentStrategy, authState.user]);

    const activePanelType = useMemo(() => {
        if (!showPersistentWorkspaceStage) {
            return null;
        }

        return rightSidebarView === RightSidebarViews.WORKSPACE ? PanelTypes.CODER : PanelTypes.PORTAL;
    }, [rightSidebarView, showPersistentWorkspaceStage]);

    /* ========================
     * handleNewChat - Creates a new conversation
     * ======================== */
    const handleNewChat = useCallback(async () => {
        const botOptions = {};
        if (currentBot && currentBot.id && currentModel && currentModel.id) {
            botOptions.botId = currentBot.id;
            botOptions.modelId = currentModel.id;
        }
        if (currentConversation?.workspaceContextId && !isPrivateWorkspaceContextId(currentConversation.workspaceContextId)) {
            botOptions.workspaceContextId = currentConversation.workspaceContextId;
        }
        await createConversation(botOptions);
    }, [createConversation, currentBot, currentModel, currentConversation]);

    /* ========================
     * handleMemoryChange - Updates conversation pruning value
     * ======================== */
    const handleMemoryChange = useCallback((value) => {
        if (!currentConversationId) return;
        updateConversation(currentConversationId, { memorySize: value }); 
    }, [currentConversationId, updateConversation]);

    /* ========================
     * handleTemperatureChange - Updates conversation temperature
     * ======================== */
    const handleTemperatureChange = useCallback((value) => {
        if (!currentConversationId) return;
        updateConversation(currentConversationId, { temperature: value });
    }, [currentConversationId, updateConversation]);


    /* ========================
     * calculateConstrainedWidth - Calculates panel width with constraints
     * ======================== */
    const calculateConstrainedWidth = useCallback((panelType, newWidthPercent) => {
        const sizingKey = getSharedPanelSizingKey(panelType);
        const config = PANEL_CONFIGS[sizingKey];
        if (!config) return newWidthPercent;
        const windowWidth = window.innerWidth;

        if (shouldUseMobileContentStrategy) {
            return Math.min(
                Math.max(newWidthPercent, config.minWidth),
                config.maxWidth
            );
        }

        const minWidthPercent = Math.max(
            config.minWidth,
            (config.minWidthPx / windowWidth) * 100
        );

        return Math.min(
            Math.max(newWidthPercent, minWidthPercent),
            config.maxWidth
        );
    }, [shouldUseMobileContentStrategy]);

    /* ========================
     * startResize - Initiates panel resizing
     * ======================== */
    const startResize = useCallback((e, panelType = activePanelType) => {
        if (!panelType) return;
        const sizingKey = getSharedPanelSizingKey(panelType);
        e.preventDefault();
        setIsResizing(true);
        setResizeStartX(e.clientX);
        setResizeStartWidth(panelWidths[sizingKey]);
        setResizingPanel(sizingKey);
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'ew-resize';
    }, [activePanelType, panelWidths]);

    /* ========================
     * handleResize - Manages panel resizing on mouse move
     * ======================== */
    const handleResizeEvent = useCallback((e) => {
        if (!isResizing || !resizingPanel) return;
        const deltaX = resizeStartX - e.clientX;
        const windowWidth = window.innerWidth;
        const newWidthPercent = resizeStartWidth + (deltaX / windowWidth * 100);
        const constrainedWidth = calculateConstrainedWidth(resizingPanel, newWidthPercent);
        setPanelWidth(resizingPanel, constrainedWidth);
    }, [isResizing, resizingPanel, resizeStartX, resizeStartWidth, setPanelWidth, calculateConstrainedWidth]);

    /* ========================
     * stopResize - Finalizes panel resizing
     * ======================== */
    const stopResize = useCallback(() => {
        setIsResizing(false);
        setResizingPanel(null);
        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }, []);

    useEffect(() => {
        if (isResizing) {
            window.addEventListener('mousemove', handleResizeEvent);
            window.addEventListener('mouseup', stopResize);

            return () => {
                window.removeEventListener('mousemove', handleResizeEvent);
                window.removeEventListener('mouseup', stopResize);
            };
        }
    }, [isResizing, handleResizeEvent, stopResize]);

    /* ========================
     * handlePanelOpen - Toggles a panel and handles notification resets
     * ======================== */
    const handlePanelOpen = useCallback((panelType) => {
        if (!isPanelOpen(panelType)) {
            const sizingKey = getSharedPanelSizingKey(panelType);
            const config = PANEL_CONFIGS[sizingKey];
            if (config) {
                const constrainedDefaultWidth = calculateConstrainedWidth(sizingKey, config.defaultWidth);
                setPanelWidth(sizingKey, constrainedDefaultWidth);
            }
        }
        if (panelType === PanelTypes.CODER) {
            resetNotification();
        }
        togglePanelWithMemory(panelType);
    }, [togglePanelWithMemory, setPanelWidth, isPanelOpen, calculateConstrainedWidth, resetNotification]);

    /* ========================
     * handleLogout - Forces logout and redirect
     * ======================== */
    const handleLogout = useCallback(async () => {
        try {
            localStorage.removeItem('currentConversationId');
            sessionStorage.clear();
            await authManager.signOut();
        } catch (error) {
            // silent
        }
        window.location.href = '/login';
    }, []);

    /* ========================
     * getPanelStyle - Generates style object for the panel CONTAINER
     * ======================== */
    const getPanelContainerStyle = useCallback((panelType) => {
        if (!isPanelOpen(panelType)) return { flexBasis: '0px', minWidth: '0px', width: '0px', overflow: 'hidden' };

        const sizingKey = getSharedPanelSizingKey(panelType);
        const config = PANEL_CONFIGS[sizingKey];
        if (!config) return { flexBasis: '0px', minWidth: '0px', width: '0px', overflow: 'hidden' };

        const widthPercent = panelWidths[sizingKey] || config.defaultWidth;

        const style = {
            flexBasis: `${widthPercent}%`,
            maxWidth: `${config.maxWidth}%`,
            flexShrink: 0,
        };
    
        if (!shouldUseMobileContentStrategy && config.minWidthPx) {
            style.minWidth = `${config.minWidthPx}px`;
        } else {
            style.minWidth = `${config.minWidth}%`;
        }
    
        return style;
    }, [panelWidths, isPanelOpen, shouldUseMobileContentStrategy]);

    const getOverlayPanelStyle = useCallback((panelType) => {
        if (!isPanelOpen(panelType)) return { width: '0px', minWidth: '0px', overflow: 'hidden' };

        const sizingKey = getSharedPanelSizingKey(panelType);
        const config = PANEL_CONFIGS[sizingKey];
        if (!config) return { width: '0px', minWidth: '0px', overflow: 'hidden' };

        const widthPercent = panelWidths[sizingKey] || config.defaultWidth;
        return {
            width: `${widthPercent}vw`,
            maxWidth: `${config.maxWidth}vw`,
            minWidth: shouldUseMobileContentStrategy ? `${config.minWidth}vw` : `${config.minWidthPx}px`,
        };
    }, [panelWidths, isPanelOpen, shouldUseMobileContentStrategy]);

    useEffect(() => {
        const handleWindowResizeForPanel = () => {
            if (shouldUseMobileContentStrategy) return;

            Object.keys(PANEL_CONFIGS).forEach((panelType) => {
                if (!isPanelOpen(panelType)) return;

                const sizingKey = getSharedPanelSizingKey(panelType);

                const currentWidth = panelWidths[sizingKey];
                const constrainedWidth = calculateConstrainedWidth(sizingKey, currentWidth);
                if (constrainedWidth !== currentWidth) {
                    setPanelWidth(sizingKey, constrainedWidth);
                }
            });
        };

        window.addEventListener('resize', handleWindowResizeForPanel);
        return () => window.removeEventListener('resize', handleWindowResizeForPanel);
    }, [panelWidths, setPanelWidth, calculateConstrainedWidth, shouldUseMobileContentStrategy, isPanelOpen]);

    const hasDisplayableChatContent = useMemo(() => {
        return currentConversation &&
               ((currentConversation.messages && currentConversation.messages.length > 0) ||
                currentConversation.status === 'awaiting_ai_response' ||
                currentConversation.status === 'inflight' ||
                currentConversation.status === 'streaming');
    }, [currentConversation]);
    

/* ========================
 * scrollDebugLogger - Logs ALL scroll events and modifications system-wide
 * Captures every scroll interaction to identify the interfering component
 * ======================== */
useEffect(() => {
  // No logging
}, []);

    const currentMemorySize = currentConversation?.memorySize ?? (tier === 'Free' ? 10 : 20);

    return (
        <div className="fixed inset-0 flex bg-background">
            <Sidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
            />

            <ToolsGuidePanel
                isOpen={isToolsGuideOpen}
                toggleOpen={() => setIsToolsGuideOpen(!isToolsGuideOpen)}
                ref={toolsGuideRef}
            />

            <main className="flex-1 flex flex-col overflow-hidden h-full">
                <Header
                    onSidebarOpen={() => setIsSidebarOpen(true)}
                    onNewChatClick={handleNewChat}
                    onSnippetsClick={() => handlePanelOpen(PanelTypes.SNIPPETS)}
                    onThemeToggle={toggleTheme}
                    onLogout={handleLogout}
                    isSnippetsOpen={isPanelOpen(PanelTypes.SNIPPETS)}
                    isMemorySliderOpen={isMemorySliderOpen}
                    onMemorySliderToggle={() => setIsMemorySliderOpen(!isMemorySliderOpen)}
                    memorySize={currentMemorySize}
                    onMemoryChange={handleMemoryChange}
                    sliderRef={memorySliderRef}
                    temperature={currentConversation?.temperature ?? currentBot?.temperature ?? currentModel?.temperatureRange?.[0] ?? 0.7}
                    onTemperatureChange={handleTemperatureChange}
                    currentConversation={currentConversation}
                    hasNewCompletion={hasNewCompletion}
                    showLogout={!showPersistentWorkspaceStage}
                />
                <div className="flex-1 flex flex-col min-h-0">
                    {hasDisplayableChatContent ? (
                        <ChatColumn />
                    ) : showKeysView ? (
                        <EmptyState
                            type="keys"
                            currentConversation={currentConversation}
                            platformNeeded={platformNeeded}
                        />
                    ) : (
                        <EmptyState
                            type="chat"
                            currentConversation={currentConversation}
                        />
                    )}
                </div>

                <InputArea
                    isPortalOpen={showPersistentWorkspaceStage}
                    canAttachFiles={true} 
                    canAttachImages={currentModel?.acceptsImages ?? false} 
                    chatFontSize={chatFontSize}
                    currentConversation={currentConversation}
                    onSendMessage={sendMessage}
                    isMemorySliderOpen={isMemorySliderOpen}
                />
            </main>

            {/* NEW: Unified Panel Container Logic */}
            {showPersistentWorkspaceStage && activePanelType && (
                <>
<div
    className="h-full flex-shrink-0 flex flex-col border-l border-border-color bg-background-dark relative"
    style={getPanelContainerStyle(activePanelType)}
>
    <div
        className="absolute left-0 top-0 h-full w-[3px] cursor-ew-resize bg-transparent z-10 select-none"
        style={{ marginLeft: '-2px' }}
        onMouseDown={startResize}
        ref={resizeRef}
    />
                        <AnimatePresence mode="wait">
                            <ShelbulaWorkspaces key="workspace-stage" onLogout={handleLogout} />
                        </AnimatePresence>
                    </div>
                </>
            )}
            {isPanelOpen(PanelTypes.SNIPPETS) && (
                <div
                    className="fixed right-0 top-0 z-40 h-full border-l border-border-color bg-surface shadow-2xl"
                    style={{
                        ...getOverlayPanelStyle(PanelTypes.SNIPPETS),
                        backgroundColor: 'var(--surface)',
                        opacity: 1,
                    }}
                >
                    <div
                        className="absolute left-0 top-0 h-full w-[3px] cursor-ew-resize bg-transparent z-10 select-none"
                        style={{ marginLeft: '-2px' }}
                        onMouseDown={(e) => startResize(e, PanelTypes.SNIPPETS)}
                    />
                    <SnippetsDrawer isOpen={true} onClose={() => handlePanelOpen(PanelTypes.SNIPPETS)} />
                </div>
            )}
            <div className={`
                fixed bottom-4 right-1 
                flex flex-col space-y-2 
                transition-all duration-300 ease-in-out
                ${(isToolsGuideOpen)
                    ? 'opacity-0 pointer-events-none translate-y-4'
                    : 'opacity-100 translate-y-0'
                }
                max-[840px]:bottom-40 
            `}>
                <button
                    onClick={() => setIsSupportOpen(true)}
                    className="p-2.5 rounded-full 
                               text-on-main-background-text hover:text-primary
                               transition-colors duration-200"
                    aria-label="Open Support"
                >
                    <MessageCircleQuestion className="w-5 h-5" />
                </button>
            </div>

            <SupportModal
                isOpen={isSupportOpen}
                onClose={() => setIsSupportOpen(false)}
            />
        </div>
    );
};


export default MainContainer;